import Axios from 'axios';
import store from '@/store';
import ApiService from '@/services/apiService';

const VERIFY_RETRY_DELAYS_MS = [1000, 2000, 4000];
const VERIFY_RETRY_JITTER_RATIO = 0.25;
const inFlightVerifyRequests = new Map();

// Use same-origin for API calls - Vue dev server proxy handles /api routing
const host = location.origin;

const httpClient = Axios.create({
  baseURL: `${host}/api/v1`,
  timeout: 5000,
  withCredentials: true,
  headers: {
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});
httpClient.defaults.cache = false;

let setAnnouncements = (tenantId, data) => {
  const now = new Date();
  store.state.app.announcements = data
    .map((announcement) => {
      // if expired set state to inactive
      const endDate = new Date(announcement.end);
      if (announcement.end && endDate < now && tenantId) {
        ApiService.tenantUpdateSystemAnnouncementState(tenantId, announcement);
        return null;
      }
      if (announcement && announcement.active) {
        return {
          ...announcement,
        };
      }
      return null;
    })
    .filter((a) => !!a);
};

const withApiKeyHeader = (apiKey, requestConfig = undefined) => {
  if (!apiKey) {
    return requestConfig;
  }

  return {
    ...(requestConfig || {}),
    headers: {
      ...((requestConfig && requestConfig.headers) || {}),
      'X-API-Key': apiKey,
    },
  };
};

const isAuthFailure = (error) => error?.isAuthError === true || error?.response?.status === 401;

const waitWithJitter = (baseMs) => {
  const jitter = baseMs * VERIFY_RETRY_JITTER_RATIO * (Math.random() * 2 - 1);
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, Math.round(baseMs + jitter))));
};

const requestVerifyWithRetry = (requestConfig, attempt = 0) =>
  httpClient.get('/auth/verify', requestConfig).catch((error) => {
    if (isAuthFailure(error) || attempt >= VERIFY_RETRY_DELAYS_MS.length) {
      throw error;
    }
    return waitWithJitter(VERIFY_RETRY_DELAYS_MS[attempt]).then(() =>
      requestVerifyWithRetry(requestConfig, attempt + 1)
    );
  });

export default {
  login({ commit, dispatch }, userData) {
    this.state.app.permissions = null;

    return new Promise((resolve, reject) => {
      commit('auth_request');
      httpClient
        .post('/auth/login', userData)
        .then((resp) => {
          commit('auth_success', {
            data: { ...resp.data, isApiKey: false },
          });

          // Hydrate full session state from verify, but do not roll back a successful login
          // if this best-effort call fails due to transient connectivity issues.
          dispatch('verify', { suppressLogoutOnError: true }).catch((error) => {
            console.error('Error hydrating session after successful login', error);
          });

          resolve(resp);
        })
        .catch((error) => {
          commit('logout');
          if (error.response) {
            reject(error.response);
          } else {
            reject(error);
          }
        });
    });
  },
  verify({ commit }, options = {}) {
    const normalizedOptions = typeof options === 'string' || options === null ? { apiKey: options } : options || {};
    const apiKey = normalizedOptions.apiKey || null;
    const suppressLogoutOnError = normalizedOptions.suppressLogoutOnError === true;
    const authRequestConfig = withApiKeyHeader(apiKey, {
      params: { domain: window.location.hostname },
    });
    const announcementsRequestConfig = withApiKeyHeader(apiKey);

    const inFlightKey = apiKey || '__session__';
    let verifyRequest = inFlightVerifyRequests.get(inFlightKey);

    if (!verifyRequest) {
      verifyRequest = requestVerifyWithRetry(authRequestConfig)
        .then(({ data }) => {
          if (!data.user || !data.isAuthenticated) {
            const error = new Error('Auth error');
            error.isAuthError = true;
            throw error;
          }

          commit('app/setFeatureFlags', data.featureFlags.flags, { root: true });

          const tenantId = data.tenantId;
          return httpClient
            .get('/user/own/announcements', announcementsRequestConfig)
            .then((announcements) => {
              if (announcements && announcements.data.length) {
                setAnnouncements(tenantId, announcements.data);
              }
              return data.user;
            })
            .catch((err) => {
              console.error('Error getting announcements', err);
            })
            .finally(() => {
              commit('auth_success', {
                data: { ...data, isApiKey: !!apiKey },
              });
            });
        })
        .finally(() => {
          inFlightVerifyRequests.delete(inFlightKey);
        });

      inFlightVerifyRequests.set(inFlightKey, verifyRequest);
    }

    return verifyRequest.catch((error) => {
      const isAuthError = error.isAuthError || error.response?.status === 401;
      if (isAuthError && error && typeof error === 'object') {
        error.isAuthError = true;
      }
      if (!suppressLogoutOnError) {
        if (isAuthError) {
          commit('logout');
        } else {
          commit('verify_failed');
        }
      }
      throw error;
    });
  },
  impersonate({ commit }, userId) {
    delete this.state.app.permissions;
    return httpClient.post('/auth/impersonate', { userId }).then(({ data }) => {
      if (!data.user) {
        throw new Error('Impersonate error');
      }
      commit('auth_success', {
        data: { ...data, isImpersonating: true },
      });
    });
  },
  restoreOriginal({ commit }) {
    return httpClient
      .post('/auth/restore')
      .then(({ data }) => {
        if (!data.user) {
          throw new Error('Auth error during restoration');
        }

        delete store.state.app.permissions;

        commit('app/setFeatureFlags', data.featureFlags.flags, { root: true });

        commit('auth_success', {
          data: { ...data, isImpersonating: false },
        });
      })
      .catch((error) => {
        console.error('Error restoring original user:', error);
        throw error;
      });
  },
  logout({ commit }, options = {}) {
    const shouldReload = options.reload !== false;
    return httpClient.get('/auth/logout').finally(() => {
      commit('logout');
      if (shouldReload) {
        window.location.reload();
      }
    });
  },
};
